import React, { useEffect } from 'react';

// Accessibility enhancer component that fixes common a11y issues
const AccessibilityEnhancer = () => {
  useEffect(() => {
    // Fix buttons without accessible names (non-intrusive)
    const fixButtonAccessibility = () => {
      // Only add aria-labels to buttons that truly need them
      const buttons = document.querySelectorAll('button:not([aria-label]):not([aria-labelledby])');
      
      buttons.forEach(button => {
        // Skip if button already has text content or title
        if (button.textContent.trim() || button.title) return;
        
        // Only add labels to icon-only buttons
        const svg = button.querySelector('svg');
        const className = button.className;
        
        // Only process buttons that clearly need accessibility labels
        if (svg || className.includes('icon')) {
          // Common button patterns - be more specific
          if (className.includes('close') || button.innerHTML.includes('×') || button.innerHTML.includes('✕')) {
            button.setAttribute('aria-label', 'Close');
          } else if (className.includes('menu') || className.includes('hamburger')) {
            button.setAttribute('aria-label', 'Toggle menu');
          } else if (className.includes('search')) {
            button.setAttribute('aria-label', 'Search');
          } else if (className.includes('play')) {
            button.setAttribute('aria-label', 'Play');
          } else if (className.includes('pause')) {
            button.setAttribute('aria-label', 'Pause');
          } else if (className.includes('next')) {
            button.setAttribute('aria-label', 'Next');
          } else if (className.includes('prev')) {
            button.setAttribute('aria-label', 'Previous');
          } else if (className.includes('expand') || className.includes('dropdown')) {
            button.setAttribute('aria-label', 'Expand options');
          } else if (className.includes('collapse')) {
            button.setAttribute('aria-label', 'Collapse');
          } else if (svg) {
            // Only add generic label if there's definitely an icon
            button.setAttribute('aria-label', 'Button');
          }
          // Don't add labels to buttons without clear icons
        }
      });
    };

    // Fix links that rely only on color (non-intrusive)
    const fixLinkAccessibility = () => {
      const links = document.querySelectorAll('a:not([aria-label]):not([aria-labelledby])');
      
      links.forEach(link => {
        // Only add subtle accessibility improvements, don't change existing styles
        const computedStyle = window.getComputedStyle(link);
        
        // Only add underline if the link has no existing visual distinction
        if (computedStyle.textDecoration === 'none' && 
            !link.className.includes('btn') && 
            !link.className.includes('button') &&
            !link.className.includes('card') &&
            !link.querySelector('img') &&
            !link.querySelector('svg')) {
          
          // Add very subtle underline that only shows on hover/focus
          // link.style.textDecoration = 'underline';
          link.style.textDecorationColor = 'transparent';
          link.style.transition = 'text-decoration-color 0.2s ease';
          
          link.addEventListener('mouseenter', () => {
            link.style.textDecorationColor = 'currentColor';
          });
          link.addEventListener('mouseleave', () => {
            link.style.textDecorationColor = 'transparent';
          });
          link.addEventListener('focus', () => {
            link.style.textDecorationColor = 'currentColor';
          });
          link.addEventListener('blur', () => {
            link.style.textDecorationColor = 'transparent';
          });
        }
      });
    };

    // Fix heading hierarchy
    const fixHeadingHierarchy = () => {
      const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
      let previousLevel = 0;
      
      headings.forEach(heading => {
        const currentLevel = parseInt(heading.tagName.charAt(1));
        
        // If heading jumps more than one level, add a warning
        if (currentLevel - previousLevel > 1 && previousLevel !== 0) {
          console.warn(`Heading hierarchy issue: ${heading.tagName} follows h${previousLevel}`, heading);
          
          // Optionally fix by adjusting the heading level
          if (currentLevel - previousLevel > 1) {
            const correctLevel = Math.min(previousLevel + 1, 6);
            const newHeading = document.createElement(`h${correctLevel}`);
            newHeading.className = heading.className;
            newHeading.innerHTML = heading.innerHTML;
            newHeading.setAttribute('data-original-level', currentLevel);
            heading.parentNode.replaceChild(newHeading, heading);
          }
        }
        
        previousLevel = currentLevel;
      });
    };

    // Improve contrast for low-contrast elements (non-intrusive)
    const improveContrast = () => {
      // Only add minimal, non-intrusive accessibility styles
      const style = document.createElement('style');
      style.textContent = `
        /* ONLY apply styles when user specifically needs high contrast */
        @media (prefers-contrast: high) {
          .text-gray-400 { color: #9ca3af !important; }
          .text-gray-500 { color: #6b7280 !important; }
          .text-gray-600 { color: #4b5563 !important; }
        }
        
        /* ONLY show focus indicators for keyboard users (not mouse clicks) */
        /* Buttons and links get visible focus indicators */
        button:focus-visible,
        a:focus-visible {
          outline: 2px solid #1AAE98 !important;
          outline-offset: 2px !important;
        }
        
        /* Remove focus outline for mouse users - buttons and links only */
        button:focus:not(:focus-visible),
        a:focus:not(:focus-visible) {
          outline: none !important;
        }
        
        /* Do NOT modify input, textarea, or select focus styles at all */
        /* Let them use their existing focus styling */
        
        /* Skip to content link (hidden by default) */
        .skip-to-content {
          position: absolute;
          top: -40px;
          left: 6px;
          background: #1AAE98;
          color: white;
          padding: 8px 16px;
          text-decoration: none;
          z-index: 1000;
          border-radius: 4px;
          font-weight: bold;
          transition: top 0.3s ease;
        }
        
        .skip-to-content:focus {
          top: 6px;
        }
        
        /* Screen reader only content */
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
      `;
      
      if (!document.querySelector('#accessibility-styles')) {
        style.id = 'accessibility-styles';
        document.head.appendChild(style);
      }
    };

    // Add skip to content link
    // const addSkipToContent = () => {
    //   if (!document.querySelector('.skip-to-content')) {
    //     const skipLink = document.createElement('a');
    //     skipLink.href = '#main-content';
    //     skipLink.textContent = 'Skip to main content';
    //     skipLink.className = 'skip-to-content';
    //     document.body.insertBefore(skipLink, document.body.firstChild);
        
    //     // Add main content landmark if it doesn't exist
    //     const main = document.querySelector('main, #main-content, [role="main"]');
    //     if (!main) {
    //       const appDiv = document.querySelector('#root > div');
    //       if (appDiv) {
    //         appDiv.id = 'main-content';
    //         appDiv.setAttribute('role', 'main');
    //       }
    //     }
    //   }
    // };

    // Add ARIA landmarks
    const addLandmarks = () => {
      // Add navigation landmark
      const nav = document.querySelector('nav:not([role])');
      if (nav) {
        nav.setAttribute('role', 'navigation');
        nav.setAttribute('aria-label', 'Main navigation');
      }
      
      // Add banner landmark to header
      const header = document.querySelector('header:not([role])');
      if (header) {
        header.setAttribute('role', 'banner');
      }
      
      // Add contentinfo landmark to footer
      const footer = document.querySelector('footer:not([role])');
      if (footer) {
        footer.setAttribute('role', 'contentinfo');
      }
    };

    // Run all fixes
    fixButtonAccessibility();
    fixLinkAccessibility();
    fixHeadingHierarchy();
    improveContrast();
    // addSkipToContent();
    addLandmarks();

    // Re-run fixes when DOM changes (for dynamic content)
    const observer = new MutationObserver((mutations) => {
      let shouldRerun = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldRerun = true;
        }
      });
      
      if (shouldRerun) {
        setTimeout(() => {
          fixButtonAccessibility();
          fixLinkAccessibility();
        }, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return null; // This component doesn't render anything
};

export default AccessibilityEnhancer;